(() => {
  window.SourceMotion?.destroy?.();

  const controller = new AbortController();
  const { signal } = controller;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const body = document.body;
  const labels = {
    close: body.dataset.motionClose || 'Close',
    openMedia: body.dataset.motionOpenMedia || 'Open media viewer',
    previous: body.dataset.motionPrevious || 'Previous media',
    next: body.dataset.motionNext || 'Next media',
    contact: body.dataset.motionContact || 'Contact us'
  };
  const revealSelector = [
    '.marketing-hero__content',
    '.section-heading',
    '.content-split__content',
    '.content-split__media',
    '.feature-card',
    '.media-card',
    '.product-card',
    '.article-card',
    '.search-card',
    '.process-step',
    '.stat',
    '.product-detail-card',
    '.video-card',
    '.faq-item'
  ].join(',');

  let observer;
  let scrollFrame;
  let lastScrollY = window.scrollY;
  let modal;
  let modalMedia;
  let modalPrevious;
  let modalNext;
  let mediaItems = [];
  let mediaIndex = 0;

  const icon = {
    close: '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    previous: '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="m15 5-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    play: '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path d="m9 6 9 6-9 6z" fill="currentColor"/></svg>',
    contact: '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-5 4v-4.5A2.5 2.5 0 0 1 4 13.5z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>'
  };

  const ensureObserver = () => {
    observer?.disconnect();
    observer = undefined;
    if (preference.matches || !('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-motion-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  };

  const reveal = (scope = document) => {
    const elements = [
      ...(scope.matches?.(revealSelector) ? [scope] : []),
      ...scope.querySelectorAll(revealSelector)
    ];
    elements.forEach((element, index) => {
      if (element.dataset.motionBound === 'true') return;
      element.dataset.motionBound = 'true';
      element.dataset.motionReveal = '';
      element.style.setProperty('--motion-delay', `${Math.min(index % 6, 5) * 55}ms`);
      const rect = element.getBoundingClientRect();
      if (preference.matches || !observer || rect.top < window.innerHeight * 0.92) {
        element.classList.add('is-motion-visible');
      } else {
        observer.observe(element);
      }
    });
  };

  const ensureModal = () => {
    if (modal) return modal;
    modal = document.createElement('dialog');
    modal.className = 'motion-modal';
    modal.setAttribute('aria-label', labels.openMedia);
    modal.innerHTML = `
      <div class="motion-modal__stage">
        <button class="motion-modal__button motion-modal__close" type="button" aria-label="${labels.close}">${icon.close}</button>
        <button class="motion-modal__button motion-modal__previous" type="button" aria-label="${labels.previous}">${icon.previous}</button>
        <div class="motion-modal__media"></div>
        <button class="motion-modal__button motion-modal__next" type="button" aria-label="${labels.next}">${icon.next}</button>
      </div>`;
    document.body.append(modal);
    modalMedia = modal.querySelector('.motion-modal__media');
    modalPrevious = modal.querySelector('.motion-modal__previous');
    modalNext = modal.querySelector('.motion-modal__next');
    modal.querySelector('.motion-modal__close').addEventListener('click', () => modal.close(), { signal });
    modalPrevious.addEventListener('click', () => showMedia(mediaIndex - 1), { signal });
    modalNext.addEventListener('click', () => showMedia(mediaIndex + 1), { signal });
    modal.addEventListener('click', (event) => {
      if (event.target === modal) modal.close();
    }, { signal });
    modal.addEventListener('close', stopModalMedia, { signal });
    modal.addEventListener('cancel', stopModalMedia, { signal });
    return modal;
  };

  const stopModalMedia = () => {
    modalMedia?.querySelectorAll('video').forEach((video) => video.pause());
    if (modalMedia) modalMedia.replaceChildren();
    document.body.classList.remove('modal-open');
  };

  const showMedia = (nextIndex) => {
    if (!mediaItems.length) return;
    mediaIndex = (nextIndex + mediaItems.length) % mediaItems.length;
    const source = mediaItems[mediaIndex];
    const clone = source.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('tabindex');
    if (clone instanceof HTMLVideoElement) {
      clone.controls = true;
      clone.autoplay = false;
    }
    modalMedia.replaceChildren(clone);
    const single = mediaItems.length < 2;
    modalPrevious.hidden = single;
    modalNext.hidden = single;
  };

  const openMedia = (items, index) => {
    mediaItems = items;
    mediaIndex = index;
    ensureModal();
    showMedia(index);
    document.body.classList.add('modal-open');
    modal.showModal();
  };

  const decorateGalleries = (scope = document) => {
    scope.querySelectorAll('.product-gallery').forEach((gallery) => {
      const items = [...gallery.querySelectorAll('img,video')];
      items.forEach((item, index) => {
        if (item.dataset.motionMedia === 'true') return;
        item.dataset.motionMedia = 'true';
        item.dataset.motionIndex = String(index);
        if (item instanceof HTMLImageElement) {
          item.tabIndex = 0;
          item.setAttribute('role', 'button');
          item.setAttribute('aria-label', `${labels.openMedia} ${index + 1}`);
        }
      });
    });
  };

  const decorateVideos = (scope = document) => {
    scope.querySelectorAll('.video-card').forEach((card) => {
      const video = card.querySelector('video');
      if (!video || card.querySelector('.motion-video-open')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'motion-video-open';
      button.dataset.motionVideoOpen = '';
      button.setAttribute('aria-label', labels.openMedia);
      button.innerHTML = icon.play;
      card.append(button);
    });
  };

  const ensureFloatingContact = () => {
    if (document.querySelector('.motion-floating-contact')) return;
    const link = document.createElement('a');
    link.className = 'motion-floating-contact';
    link.href = body.dataset.motionContactUrl || '/';
    link.setAttribute('aria-label', labels.contact);
    link.innerHTML = icon.contact;
    document.body.append(link);
  };

  const decorate = (scope = document) => {
    reveal(scope);
    decorateGalleries(scope);
    decorateVideos(scope);
  };

  const updateHeader = () => {
    scrollFrame = undefined;
    const header = document.querySelector('.site-header');
    if (!header) return;
    const current = window.scrollY;
    header.classList.toggle('is-scrolled', current > 24);
    const movingDown = current > lastScrollY + 4;
    const movingUp = current < lastScrollY - 4;
    if (current > 160 && movingDown && !document.body.classList.contains('menu-open')) {
      header.classList.add('is-scroll-hidden');
    } else if (movingUp || current <= 160) {
      header.classList.remove('is-scroll-hidden');
    }
    lastScrollY = current;
  };

  const requestHeaderUpdate = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(updateHeader);
  };

  const onDocumentClick = (event) => {
    const videoButton = event.target.closest('[data-motion-video-open]');
    if (videoButton) {
      const video = videoButton.closest('.video-card')?.querySelector('video');
      if (video) openMedia([video], 0);
      return;
    }
    const image = event.target.closest('.product-gallery img');
    if (!image) return;
    const gallery = image.closest('.product-gallery');
    const items = [...gallery.querySelectorAll('img,video')];
    openMedia(items, items.indexOf(image));
  };

  const onDocumentKeydown = (event) => {
    if (modal?.open) {
      if (event.key === 'ArrowLeft') showMedia(mediaIndex - 1);
      if (event.key === 'ArrowRight') showMedia(mediaIndex + 1);
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('.product-gallery img')) {
      event.preventDefault();
      event.target.click();
    }
  };

  const onPreferenceChange = () => {
    ensureObserver();
    if (preference.matches) {
      document.querySelectorAll('[data-motion-reveal]').forEach((element) => element.classList.add('is-motion-visible'));
    } else {
      decorate();
    }
  };

  ensureObserver();
  decorate();
  ensureFloatingContact();
  document.documentElement.classList.add('motion-enabled');
  updateHeader();

  window.addEventListener('scroll', requestHeaderUpdate, { passive: true, signal });
  document.addEventListener('click', onDocumentClick, { signal });
  document.addEventListener('keydown', onDocumentKeydown, { signal });
  document.addEventListener('shopify:section:load', (event) => decorate(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-motion-reveal]').forEach((element) => observer?.unobserve(element));
  }, { signal });
  preference.addEventListener('change', onPreferenceChange, { signal });

  window.SourceMotion = {
    destroy() {
      controller.abort();
      observer?.disconnect();
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      modal?.remove();
      document.querySelector('.motion-floating-contact')?.remove();
      document.documentElement.classList.remove('motion-enabled');
    }
  };
})();
